import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Box } from '@components/Box';
import { Columns } from '@components/Columns';
import { Flex } from '@components/Flex';
import { Text } from '@components/typography';
import { getUseTemplateCtaId, getSignUpScenario } from '@utils/gallery.util';
import { getGalleryCreateWeddingUrl } from '@utils/urlHelpers.util';
import { capitalize } from '@utils/strings.util';
import {
  StyledModal,
  StyledInnerContainer,
  StyledColumns,
  StyledCloseButton,
  StyledCloseIcon,
  ECardImageContainer,
  ECardImageWrapper,
  ECardImage,
  StyledThemeJoykitIcon,
  StyledButton,
  TextColumnWrapper,
  RightFlex,
  CardTitleMobileWrapper,
  CardTitleDesktopWrapper,
  LeftFlex,
  StartWithMobileButtonWrapper,
  StartWithDesktopButtonWrapper,
  CloseButtonMobileWrapper,
  CloseButtonDesktopWrapper,
} from './PhotoECardModal.styles';

import { segment } from 'static/js/joy';
import { useTranslation } from 'react-i18next';

export interface PhotoECardModalProps
  extends Readonly<{
    cardDetails: GalleryECard;
    isModalOpen: boolean;
    handleClose: () => void;
    productType: GalleryProductType;
  }> {}

const DEFAULT_ASPECT_RATIO = 0.72;

const getAspectRatio = (imageEl: HTMLImageElement | undefined) => {
  let aspectRatio = DEFAULT_ASPECT_RATIO;
  if (imageEl) {
    aspectRatio = imageEl.naturalWidth / imageEl.naturalHeight;
  }
  return aspectRatio;
};

const getSelectedVariantData = (
  initialActiveVariantFilter: GalleryECard,
  initialActiveProductType: GalleryProductType,
  cardDetails: GalleryECard,
  apiProductTypes: Record<string, string>
) => {
  const activeVariants = cardDetails.theme?.marketingData.filter(
    (item) =>
      item.theme.themeId === initialActiveVariantFilter.variantThemeId && item.variationType === apiProductTypes[initialActiveProductType]
  );
  return activeVariants?.[0] ?? cardDetails;
};

export const PhotoECardModal: React.FC<PhotoECardModalProps> = ({ cardDetails, isModalOpen, handleClose, productType }) => {
  const [aspectRatio, setAspectRatio] = useState(DEFAULT_ASPECT_RATIO);
  const [isLoaded, setLoaded] = useState(false);
  const [activeProductType, setActiveProductType] = useState<GalleryProductType>(productType);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const signUpScenario = getSignUpScenario(productType);

  const { t } = useTranslation('galleryPage');

  const eCardDetailData = t('eCardDetailData');
  const apiProductTypes = t('apiProductTypes');
  const eventProductTypes = t('eventProductTypes');

  useEffect(() => {
    const imageEl = wrapperRef.current?.querySelector('img');
    if (imageEl?.complete) {
      onImageLoaded(imageEl);
    }
    if (productType) {
      setActiveProductType(productType);
    }
  }, [productType]);

  const onImageLoaded = (imageEl: HTMLImageElement) => {
    setAspectRatio(getAspectRatio(imageEl));
    setLoaded(true);
  };

  const activeVariantFilter = useMemo(
    () => cardDetails.theme.marketingData.filter((item) => item.theme.themeId === cardDetails.theme.themeId)?.[0],
    [cardDetails.theme.marketingData, cardDetails.theme.themeId]
  );

  const [selectedVariant, setSelectedVariant] = useState<GalleryECard>(cardDetails);
  const themeId = selectedVariant.variantThemeId ? selectedVariant.variantThemeId : selectedVariant.theme.themeId;

  useEffect(() => {
    setSelectedVariant(getSelectedVariantData(activeVariantFilter, activeProductType, cardDetails, apiProductTypes));
  }, [setSelectedVariant, activeVariantFilter, activeProductType, cardDetails, apiProductTypes]);

  return (
    <StyledModal
      isOpen={isModalOpen}
      onRequestClose={handleClose}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      ariaHideApp={false}
      style={{
        overlay: {
          background: 'rgba(0, 0, 0, 0.25)',
          zIndex: 101,
          overflow: 'hidden',
          overflowY: 'scroll',
        },
        content: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        },
      }}
    >
      <StyledInnerContainer ref={containerRef} alignItems="center" justifyContent="center">
        <StyledColumns centered>
          <Columns.Column width={6}>
            <LeftFlex flexDirection="column" alignItems="center">
              <CloseButtonMobileWrapper>
                <StyledCloseButton
                  id="photo-ecard-mobile-close"
                  variant="altPurpleSecondary"
                  onClick={handleClose}
                  skipRedirectWithTelemetry={true}
                >
                  <StyledCloseIcon name="notAttending" color="mono1" />
                </StyledCloseButton>
              </CloseButtonMobileWrapper>
              <CardTitleMobileWrapper marginBottom={[6]} alignItems="center" flexDirection="column">
                <Text color="neutra4" variant="featureHed17" tagName="h1">
                  {eCardDetailData.freePhotoSavetheDates}
                </Text>
                <Text color="black" variant="hed40" tagName="h1">
                  {capitalize(selectedVariant.theme.name)}
                </Text>
              </CardTitleMobileWrapper>
              <ECardImageContainer>
                <ECardImageWrapper ref={wrapperRef} aspectRatio={aspectRatio}>
                  <ECardImage
                    src={selectedVariant.imageUrl}
                    alt={selectedVariant.theme.name}
                    isLoaded={isLoaded}
                    onLoad={({ currentTarget }) => {
                      onImageLoaded(currentTarget);
                    }}
                  />
                </ECardImageWrapper>
              </ECardImageContainer>
            </LeftFlex>
          </Columns.Column>
          <Columns.Column width={6}>
            <RightFlex flexDirection="column" paddingBottom={[11, 0]}>
              <StartWithMobileButtonWrapper>
                <StyledButton
                  id={getUseTemplateCtaId(selectedVariant.variationType, themeId)}
                  variant="primary"
                  forwardedAs={'a'}
                  target="_blank"
                  telemetryLabel={themeId}
                  href={getGalleryCreateWeddingUrl(cardDetails, signUpScenario, selectedVariant.variationType, themeId)}
                >
                  {eCardDetailData.getStarted}
                </StyledButton>
              </StartWithMobileButtonWrapper>
              <TextColumnWrapper flexDirection="column">
                <CardTitleDesktopWrapper flexDirection="column">
                  <Text color="neutra4" variant="featureHed17" tagName="h1">
                    {eCardDetailData.freePhotoSavetheDates}
                  </Text>
                  <Text color="black" variant="hed40" tagName="h1">
                    {capitalize(selectedVariant.theme.name)}
                  </Text>
                </CardTitleDesktopWrapper>
                <Box marginTop={10}>
                  <Flex alignItems="center" marginBottom={5}>
                    <StyledThemeJoykitIcon name="photos" color="mono1" />
                    <Text marginLeft={4} tagName="span" color="mono1" variant="featureCopy17">
                      {eCardDetailData.featureAnyPhoto}
                    </Text>
                  </Flex>
                  <Flex alignItems="center" marginBottom={5}>
                    <StyledThemeJoykitIcon name="connectedGuestList" color="warmGray1" />
                    <Text marginLeft={4} tagName="span" color="mono1" variant="featureCopy17">
                      {eCardDetailData.poweredBy}
                    </Text>
                  </Flex>
                  <Flex alignItems="center">
                    <StyledThemeJoykitIcon name="eCardRSVP" color="warmGray1" />
                    <Text marginLeft={4} tagName="span" color="mono1" variant="featureCopy17">
                      {eCardDetailData.rsvpIncludes}
                    </Text>
                  </Flex>
                </Box>
                <StartWithDesktopButtonWrapper marginTop={10}>
                  <StyledButton
                    id={getUseTemplateCtaId(selectedVariant.variationType, themeId)}
                    variant="primary"
                    forwardedAs={'a'}
                    target="_blank"
                    telemetryLabel={themeId}
                    href={getGalleryCreateWeddingUrl(cardDetails, signUpScenario, selectedVariant.variationType, themeId)}
                    onClick={() => {
                      segment.sendTelemetryFilterEvent('StartCreate', {
                        type: 'track',
                        category: `newmarketing.${eventProductTypes[productType]}`,
                        action: 'StartCreate',
                        label: themeId,
                        extraInfo: {
                          activeFilterThemeType: selectedVariant.tags.toString(),
                          url: getGalleryCreateWeddingUrl(cardDetails, signUpScenario, selectedVariant.variationType, themeId),
                          template: selectedVariant.variationType,
                          style: themeId,
                        },
                      });
                    }}
                  >
                    {eCardDetailData.getStarted}
                  </StyledButton>
                </StartWithDesktopButtonWrapper>
              </TextColumnWrapper>
            </RightFlex>
          </Columns.Column>
          <CloseButtonDesktopWrapper>
            <StyledCloseButton id="photo-ecard-close" variant="altPurpleSecondary" onClick={handleClose} skipRedirectWithTelemetry={true}>
              <StyledCloseIcon name="notAttending" color="mono1" />
            </StyledCloseButton>
          </CloseButtonDesktopWrapper>
        </StyledColumns>
      </StyledInnerContainer>
    </StyledModal>
  );
};
